import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { isDemoZone } from '../components/demo/constants';
import { makeAuthenticatedGetRequest } from '../services/axios';
import { getUserInfo } from '../services/axios/endpoints';
import Sentry from '../services/sentry';
import { CLIENT_APPLE, userMeResponseSchema } from '../stores/schemas/userMe';
import type { CompassModule, UserMeResponse } from '../stores/schemas/userMe';
import { useGlobalStore } from '../stores/useGlobalStore';
import { getUserType } from '../utils/functions/getUserType';

interface ApiError extends Error {
  response?: {
    data?: any;
    status?: number;
  };
}

export interface PermissionsContextValue {
  permissions: string[];
  loading: boolean;
  isFreemiumUser: boolean;
  currentPlan: string | null;
  compassModules: CompassModule[];
  compassModulesMap: Record<string, CompassModule>;
  whitelistedProjectTypes: string[];
  isExpertUser: boolean;
  userData: UserMeResponse | null;
  isEarlyStage: boolean;
  isCompassEnabled: boolean;
  latestAppVersion: string;
  isVersionMismatch: boolean;
  isDemoUser: boolean;
  isAppleClient: boolean;
}

export const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: [],
  loading: true,
  isFreemiumUser: true,
  currentPlan: null,
  compassModules: [],
  compassModulesMap: {},
  whitelistedProjectTypes: [],
  isExpertUser: false,
  userData: null,
  isEarlyStage: false,
  isCompassEnabled: false,
  latestAppVersion: '',
  isVersionMismatch: false,
  isDemoUser: false,
  isAppleClient: false,
});

const BLACKLISTED_ROUTES = ['/register', '/signup', '/account-signup', '/account-request', '/demo'];

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const isBlacklistedRoute = BLACKLISTED_ROUTES.includes(window.location.pathname);
  const user = useGlobalStore((s) => s.user);
  const updateUser = useGlobalStore((s) => s.updateUser);
  const demoSession = useGlobalStore((s) => s.demoSession);
  const [previousSuccessfulData, setPreviousSuccessfulData] = useState<UserMeResponse | null>(null);

  const { data, isLoading, error, isError, failureCount } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => {
      const res = await makeAuthenticatedGetRequest(getUserInfo);
      return userMeResponseSchema.parse(res?.data);
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Never hit /user/me in the demo zone — demo authenticates via the demo
    // cookie, and its access is derived from the demo session (/user/demo/me).
    enabled: !isBlacklistedRoute && !isDemoZone,
  });

  useEffect(() => {
    if (data) {
      setPreviousSuccessfulData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        tags: {
          api: 'user-me',
          context: 'permissions-context',
        },
        extra: {
          errorMessage: error?.message,
          errorResponse: apiError?.response?.data,
          statusCode: apiError?.response?.status,
          failureCount,
          hasPreviousData: !!previousSuccessfulData,
        },
      });
    }
  }, [isError, error, failureCount, previousSuccessfulData]);

  const activeData = data || previousSuccessfulData;

  useEffect(() => {
    if (!data || !user) return;

    const updates: Record<string, unknown> = {};

    if (data.company_type && user.company_type !== data.company_type) {
      updates.company_type = data.company_type;
    }

    const compassModulesAccess =
      data.compass_modules?.map(({ display_name }) => display_name) || [];
    if (JSON.stringify(user.compass_modules_access) !== JSON.stringify(compassModulesAccess)) {
      updates.compass_modules_access = compassModulesAccess;
    }

    if (data.plan && user.ratings_access_type !== data.plan) {
      updates.ratings_access_type = data.plan;
    }

    const calculatedUserType = getUserType(user.company, data.is_demo, data.is_plan_public);
    if (user.user_type !== calculatedUserType) {
      updates.user_type = calculatedUserType;
    }

    if (
      data.compass_subscription_type &&
      user.compass_access_type !== data.compass_subscription_type
    ) {
      updates.compass_access_type = data.compass_subscription_type;
    }

    if (Object.keys(updates).length > 0) {
      updateUser(updates as Partial<NonNullable<typeof user>>);
    }
  }, [data, user, updateUser]);

  const compassModulesMap = useMemo(() => {
    return ((activeData?.compass_modules ?? []) as CompassModule[]).reduce<
      Record<string, CompassModule>
    >((acc, mod) => {
      acc[mod.name] = mod;
      return acc;
    }, {});
  }, [activeData?.compass_modules]);

  const whitelistedProjectTypes = useMemo(() => {
    return (
      activeData?.compass_modules
        ?.filter((mod) => mod.has_permission)
        ?.map((mod) => mod.project_types?.[0]?.name) || []
    );
  }, [activeData?.compass_modules]);

  const isExpertUser = useMemo(() => {
    return activeData?.plan?.toLowerCase()?.includes('expert') ?? false;
  }, [activeData?.plan]);

  // In the demo zone /user/me may 401 (no PropelAuth); don't crash the app —
  // the demo cookie authorizes the feature calls regardless.
  if (isError && !previousSuccessfulData && !isBlacklistedRoute && !isDemoZone) {
    throw new Error('Failed to load user permissions. Please try again later.');
  }

  return (
    <PermissionsContext.Provider
      value={{
        permissions: isDemoZone
          ? demoSession
            ? [demoSession.feature_key]
            : []
          : [
              ...(activeData?.features?.map((f) => f.name) || []),
              ...(activeData?.screener_access ? ['dashboard:project_screener'] : []),
            ],
        loading: isLoading,
        isFreemiumUser: activeData?.is_plan_public ?? activeData?.freemium_experience ?? true,
        currentPlan: activeData?.plan ?? null,
        compassModules: activeData?.compass_modules || [],
        compassModulesMap,
        whitelistedProjectTypes,
        isExpertUser,
        userData: activeData ?? null,
        isEarlyStage: !activeData?.compass,
        isCompassEnabled: activeData?.compass ?? false,
        latestAppVersion: activeData?.latest_ui_version ?? '',
        isVersionMismatch: Boolean(
          activeData?.latest_ui_version &&
            process.env.GITHUB_VERSION_NUMBER &&
            process.env.GITHUB_VERSION_NUMBER !== activeData?.latest_ui_version
        ),
        isDemoUser: isDemoZone || (activeData?.is_demo ?? false),
        isAppleClient: activeData?.client === CLIENT_APPLE,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
